import { useEffect } from "react";

/** Production origin. Canonical URLs must be absolute, so this can't be relative. */
export const SITE_ORIGIN = "https://parcelyapp.com";

interface SeoMeta {
  /** Sets document.title. */
  title?: string;
  /** Sets <meta name="description">. */
  description?: string;
  /**
   * Overrides the canonical URL. Accepts a path ("/pricing") or an absolute
   * URL; both normalize through canonicalUrlFor. Omit it — the default,
   * deriving from the CURRENT path, is what almost every route wants, and it
   * is the only thing that stays correct for the parameterized routes
   * (/glossary/:slug, /public/:countyId, /learn/:propertyType) without every
   * caller re-deriving its own slug. Pass this only to point one page at a
   * different one, which nothing does today: the per-county guides
   * deliberately self-canonicalize (CountyTaxForeclosureGuide's header calls
   * the Mecklenburg guide "the canonical deep page" in the editorial sense —
   * where the statutory walkthrough lives — NOT as a rel=canonical target;
   * those pages carry county-specific framing and are meant to rank on their
   * own).
   */
  canonical?: string;
  /**
   * When true, sets <meta name="robots" content="noindex, follow"> so
   * search engines don't index this route — e.g. a per-county landing page
   * that hasn't cleared the lead-volume tiering bar yet (see
   * COUNTY_LANDING_LEAD_THRESHOLD in lib/publicCountyStats.ts). Defaults to
   * indexable (no tag is created at all unless some route needs noindex).
   * If an EARLIER route in this SPA session set noindex and the current one
   * doesn't, the tag is reset to "index, follow" rather than left behind
   * stale — client-side navigations reuse the same <head>, so a tag one
   * route creates otherwise leaks forward to every route after it.
   */
  noindex?: boolean;
}

/**
 * Normalizes a path or absolute URL into the canonical absolute form:
 * query string and hash dropped, duplicate slashes collapsed, trailing
 * slash removed everywhere except the root.
 *
 * Dropping the query string is the point, not a detail — without it every
 * ?utm_source=/?fbclid= variant of a page declares ITSELF canonical and
 * splits that page's ranking signals across as many URLs as there are
 * campaigns. Trailing-slash stripping matches how public/sitemap.xml writes
 * these same URLs; canonical and sitemap disagreeing is its own weak signal.
 *
 * Case is deliberately preserved. React Router v6 matches case-sensitively,
 * so /Pricing never renders Pricing — it falls through to the catch-all,
 * which does not call this hook. Lowercasing here would let a page emit a
 * canonical pointing at a URL that renders something else.
 */
export function canonicalUrlFor(pathOrUrl: string): string {
  let path = pathOrUrl;
  if (/^https?:\/\//i.test(path)) {
    // Absolute: keep only the path. Deliberately not `new URL(...).pathname`
    // on the raw input — a caller passing a foreign origin should still
    // canonicalize onto ours, never off-site.
    path = path.replace(/^https?:\/\/[^/]*/i, "");
  }
  path = path.split("?")[0].split("#")[0];
  path = "/" + path.replace(/^\/+/, "").replace(/\/{2,}/g, "/");
  if (path.length > 1) path = path.replace(/\/+$/, "");
  return SITE_ORIGIN + path;
}

/** Creates the tag if absent, then sets `attr` to `value`. */
function upsertHeadTag(
  selector: string,
  create: () => HTMLElement,
  attr: string,
  value: string,
): void {
  let tag = document.head.querySelector<HTMLElement>(selector);
  if (!tag) {
    tag = create();
    document.head.appendChild(tag);
  }
  tag.setAttribute(attr, value);
}

/**
 * Lightweight, dependency-free per-route SEO. Sets document.title, the meta
 * description, and the canonical URL on mount/update so each public route
 * presents distinct metadata to crawlers.
 *
 * CRA is client-rendered (no SSR), so this reaches crawlers that execute
 * JS — Googlebot does. Social link unfurls (Facebook/LinkedIn/Twitter do
 * NOT run JS) fall back to the static defaults in public/index.html; full
 * per-route social cards would require prerendering (separate effort,
 * deferred to #332 per the founder's direction on #155).
 *
 * The canonical tag is the one piece here that is worse than useless when
 * absent. public/index.html ships a hardcoded
 * <link rel="canonical" href="https://parcelyapp.com/">, and a CRA SPA
 * serves that same index.html for EVERY route — so before this hook set it,
 * all ~45 sitemap URLs told Google they were duplicates of the homepage,
 * which is an explicit request to drop them from the index. og:url gets the
 * same treatment for the same reason: leaving it pointing at "/" while
 * canonical says otherwise is a self-contradicting head. og:title and
 * og:description are deliberately NOT updated here — their only consumers
 * are the JS-less social scrapers this hook can't reach anyway, so changing
 * them would add code that no client can observe.
 */
export function useSeo({ title, description, canonical, noindex }: SeoMeta): void {
  useEffect(() => {
    if (title) document.title = title;
    if (description) {
      let tag = document.head.querySelector<HTMLMetaElement>('meta[name="description"]');
      if (!tag) {
        tag = document.createElement("meta");
        tag.name = "description";
        document.head.appendChild(tag);
      }
      tag.content = description;
    }

    // Always set, never conditional: an unset canonical means the stale one
    // from index.html (or from the previously-viewed route, since client-side
    // navigation reuses this <head>) stands, which is the exact bug this
    // fixes. window.location is safe here — the effect only runs in a
    // browser, and every test in this repo runs under jsdom.
    const href = canonicalUrlFor(canonical ?? window.location.pathname);
    upsertHeadTag(
      'link[rel="canonical"]',
      () => {
        const el = document.createElement("link");
        el.setAttribute("rel", "canonical");
        return el;
      },
      "href",
      href,
    );
    upsertHeadTag(
      'meta[property="og:url"]',
      () => {
        const el = document.createElement("meta");
        el.setAttribute("property", "og:url");
        return el;
      },
      "content",
      href,
    );

    let robots = document.head.querySelector<HTMLMetaElement>('meta[name="robots"]');
    if (noindex) {
      if (!robots) {
        robots = document.createElement("meta");
        robots.name = "robots";
        document.head.appendChild(robots);
      }
      robots.content = "noindex, follow";
    } else if (robots) {
      robots.content = "index, follow";
    }
  }, [title, description, canonical, noindex]);
}
